/* eslint-disable no-debugger */
// ---------------------------------------------------------------------------
// Scheduling-call rule — single source of truth shared by:
//   • Proposal Tool client flow (utils/client-proposal.util.js →
//     isScheduleCallRequired, ClientProposalSchedule's productGroupCodes)
//   • Account Setup / Purchase Link flow (AccountSetup/utils/
//     shouldRequireOnboardingCall → getSchedulingLocations, decided PER
//     LOCATION; useOnboardingScheduling's productGroupCodes)
//
// Product reference: docs/onboarding-scheduled-calls.md
// Legacy origin:     src/screens/onboarding-program.js
//                    (shouldDisplayKickOffCall / comparePlans)
//
// Both flows feed this the same item shape:
//   { productCode, productType: 'CENTER'|'APP'|'ONE_TIME_FEE',
//     alreadyIncluded?, addons?: [{productCode, alreadyIncluded?}],
//     entitlements?: [{code, alreadyIncluded?}],          // proposal only
//     configuration? | configuredOptions?: {onboarding?, includeOnboarding?},
//     onboardingRequired?: {value}, includeOnboarding?: {value} }   // legacy
//
// Only NEWLY added items (!alreadyIncluded) count — mirrors comparePlans'
// "selected but not already present" semantics for upgrades, and is a no-op
// for brand-new accounts where nothing is alreadyIncluded.
//
// Deliberately NOT ported (no data to drive them in either new flow):
//   • comparePlans' product `name` / `groupTitle` catch-all — catalog items
//     carry no groupTitle; every product that needs a call must be listed
//     explicitly below (the legacy code says the same).
//   • Legacy-Keap "Hidden" groupTitle exemption — same reason.
//
// Strategic Content Creation's separate Thryv Success Services call IS
// modelled — see CALL_TYPES / getRequiredCallTypes at the bottom. The gate
// (itemsRequireScheduleCall) treats it like any other trigger; which NAMED
// call(s) to show is a second question answered by getRequiredCallTypes.
// Account Setup's Scheduling step uses both; the Proposal Tool client flow
// currently uses the gate only and books the single Onboarding call.
// ---------------------------------------------------------------------------
import {
  MC_2_0_SIGNATUREPLANS,
  THRYV_STARTER_CODES,
  GROWTH_PACKAGES,
} from './constants'
import {THRYV_BASIC_CODES} from './tgp-catalog-flags'

/**
 * App / addon / one-time product codes that always require a scheduling call
 * when newly added. Centers are handled by the generic CENTER productType
 * check; codes here cover everything that is NOT a center (plus a couple of
 * centers kept as safeguards).
 *
 * Docs reference: §1 (Kickoff), §2 (TSS), §3 (One-Time)
 */
export const APPOINTMENT_REQUIRED_CODES = new Set([
  // --- AEO & SEO ---
  'seobase-5-thryv-m2m',
  // --- Thryv Leads ---
  'tl-mc-5-thryv-m2m',
  // --- Social Ads (Bronze / Silver / Gold) ---
  'addon-socialadsbronze-5-m2m',
  'addon-socialadsgold-5-m2m',
  'addon-socialadssilver-5-m2m',
  // --- Social Content Generation ---
  'addon-6-mc-socialcontentgen-m2m',
  // --- Workforce Center Plus (also a CENTER — kept as safeguard) ---
  'wc_plus-5-thryv-m2m',
  // --- Home Services (evaluated as APPs in onboarding-program.js) ---
  'maxhvac-5-thryv-m2m', // Max HVAC
  'maxroof-5-thryv-m2m', // Max Roofing
  'maxelect-5-thryv-m2m', // Max Electrical
  'maxplumb-5-thryv-m2m', // Max Plumbing
  // --- Leads Lift ---
  'addon-5-leadslift-m2m',
  // --- AI Visibility Boost (FULSAS-12281 / BUSREQ-15061) ---
  'addon-5-visibilityboost',
  // --- Strategic Content Creation (Thryv Success Services call) ---
  'addon-5-stratcontent-m2m',
  // --- Website products (FULSAS-11317) ---
  'addon-5-website_difm', // Standalone Website DIFM
  'thryv5page', // 5 Extra Website Pages (one-time)
  // --- One-time design / creative items (FULSAS-11317) ---
  'thryvlogo4', // Professionally Designed Logo
  'addon-5-custvid', // Custom Video Shoot
  'thryvdesg2', // Legacy design product
])

/**
 * Center codes that never require a scheduling call on their own.
 * Evaluated by the generic CENTER productType check and must be explicitly
 * exempted here to avoid false positives.
 *
 * Docs reference: §7 (No-Kickoff Products)
 */
export const APPOINTMENT_EXEMPT_CENTER_CODES = new Set([
  // MC Foundation is the $0 tier — auto-added alongside SEO/Thryv Leads for
  // BC-only accounts; never needs a kickoff call by itself.
  'mc_foundation-5-thryv-m2m',
  ...THRYV_BASIC_CODES,
  // Thryv Starter plans — exempt unless the Growth Package "Include
  // Onboarding?" opt-in is Yes (FULSAS-12295, see hasStarterOnboardingOptIn).
  ...THRYV_STARTER_CODES,
  // Free-account center codes — no onboarding required
  'appmarket-5-thryv-m2m',
  'cc_basicpls-5-thryv-m2m',
  'cc_basic-5-thryv-m2m',
])

/**
 * Entitlement codes that require a kickoff call when newly added to a center,
 * including a center the account already holds (upgrade adding e.g. a
 * bundled website to an existing MC Signature).
 *
 * Docs reference: §4 (Entitlements)
 */
export const APPOINTMENT_REQUIRED_ENTITLEMENT_CODES = new Set([
  'ent-5-website', // Professionally Designed Website
  'ent-5-custvideo', // Custom Video
  'ent-5-socialcontentgen', // Social Content Generation
])

const yes = v =>
  typeof v === 'string' ? v.toLowerCase() === 'yes' : v === true
const no = v => (typeof v === 'string' ? v.toLowerCase() === 'no' : v === false)

function getItemConfig(item) {
  return item?.configuration ?? item?.configuredOptions ?? {}
}

// MC Signature "onboarding required?" — Proposal Tool stores the client's
// choice as configuration.onboarding ('yes'|'no'); the legacy catalog attaches
// onboardingRequired: {value: 'Yes'|'No'} onto the cart entry. Accept both.
function isOnboardingDeclined(item) {
  const cfg = getItemConfig(item)
  return no(cfg.onboarding) || no(item?.onboardingRequired?.value)
}

// Thryv Starter "Include Onboarding?" — lives on the Growth Package add-on
// (top-level APP or nested under the Starter center), not on the center.
// Same dual-shape tolerance as isOnboardingDeclined.
function isOnboardingOptedIn(addon) {
  const cfg = getItemConfig(addon)
  return (
    yes(cfg.includeOnboarding) ||
    yes(cfg.includeOnboarding?.value) ||
    yes(addon?.includeOnboarding?.value)
  )
}

function hasCode(item, code) {
  return (
    (item.entitlements ?? []).some(ent => ent.code === code) ||
    (item.addons ?? []).some(addon => addon.productCode === code)
  )
}

/**
 * FULSAS-12295: Thryv Starter is exempt from a kickoff call UNLESS a Growth
 * Package add-on in the same cart has its onboarding opt-in set to Yes.
 * Checked before the per-item rules so it can force the call on.
 *
 * Docs reference: §9 (Conditional Kickoff — Thryv Starter)
 */
export function hasStarterOnboardingOptIn(items = []) {
  const starterCenter = items.find(
    item =>
      !item.alreadyIncluded && THRYV_STARTER_CODES.includes(item.productCode),
  )
  if (!starterCenter) return false

  const growthPackages = [
    ...items.filter(item => GROWTH_PACKAGES.includes(item.productCode)),
    ...items.flatMap(item =>
      (item.addons ?? []).filter(addon =>
        GROWTH_PACKAGES.includes(addon.productCode),
      ),
    ),
  ]
  return growthPackages.some(isOnboardingOptedIn)
}

/**
 * Does this single cart item (with its nested addons/entitlements) require a
 * scheduling call?
 *
 * Evaluation order (mirrors onboarding-program.js / Proposal Tool):
 *  1. MC Signature conditional kickoff — required unless onboarding was
 *     declined AND no 'ent-5-website' entitlement/addon is bundled.
 *  2. Generic new-CENTER check — any new, non-exempt, non-declined center.
 *  3. New kickoff entitlement added to any non-exempt center (including an
 *     alreadyIncluded center in an upgrade).
 *  4. App / add-on / one-time product code check.
 *  5. Nested addon code check (required codes OR kickoff entitlement codes).
 */
export function itemRequiresScheduleCall(
  item,
  {isTgpUpgradeEnabled = false} = {},
) {
  if (!item) return false
  const isNew = !item.alreadyIncluded
  const onboardingDeclined = isOnboardingDeclined(item)
  const isExemptCenter = APPOINTMENT_EXEMPT_CENTER_CODES.has(item.productCode)
  // (0) GEP-7133 / FULSAS-13599: TGP upgrade flow — an explicit
  // `configuration.onboarding: 'no'` means this item never requires a call,
  // full stop, independent of the Signature-specific website-bundling
  // override in (1) below. Only takes effect when the caller explicitly
  // passes isTgpUpgradeEnabled: true — every existing caller that doesn't
  // pass it defaults to false, so this is a no-op everywhere else.
  // TO REVERT: delete this block and the isTgpUpgradeEnabled param above.
  // if (isTgpUpgradeEnabled && no(getItemConfig(item).onboarding)) {
  //   return false
  // }

  // (1) MC Signature conditional kickoff — must short-circuit before (2).
  if (isNew && MC_2_0_SIGNATUREPLANS.includes(item.productCode)) {
    return !onboardingDeclined || hasCode(item, 'ent-5-website')
  }

  // (2) Generic CENTER check
  if (
    isNew &&
    item.productType === 'CENTER' &&
    !onboardingDeclined &&
    !isExemptCenter
  ) {
    return true
  }

  // (3) New kickoff entitlement on a (possibly already-owned) center.
  //     Proposal data carries these under `entitlements`; Account Setup's
  //     catalog nests them under `addons` — check both.
  if (!isExemptCenter) {
    const hasNewKickoffEntitlement =
      (item.entitlements ?? []).some(
        ent =>
          !ent.alreadyIncluded &&
          APPOINTMENT_REQUIRED_ENTITLEMENT_CODES.has(ent.code),
      ) ||
      (item.addons ?? []).some(
        addon =>
          !addon.alreadyIncluded &&
          APPOINTMENT_REQUIRED_ENTITLEMENT_CODES.has(addon.productCode),
      )
    if (hasNewKickoffEntitlement) return true
  }

  // (4) Product code check — apps, add-ons, one-time items
  if (isNew && APPOINTMENT_REQUIRED_CODES.has(item.productCode)) return true

  // (5) Nested addon code check
  return (item.addons ?? []).some(
    addon =>
      !addon.alreadyIncluded &&
      APPOINTMENT_REQUIRED_CODES.has(addon.productCode),
  )
}

/**
 * Does this cart (flat items array for ONE proposal option / ONE location)
 * require a scheduling call?
 */
export function itemsRequireScheduleCall(
  items = [],
  {isTgpUpgradeEnabled = false} = {},
) {
  if (!Array.isArray(items) || items.length === 0) return false
  if (hasStarterOnboardingOptIn(items)) return true

  return items.some(item =>
    itemRequiresScheduleCall(item, {isTgpUpgradeEnabled}),
  )
}

// ---------------------------------------------------------------------------
// Which NAMED call(s) a cart needs — ports the legacy onboarding-program.js
// call-type split (docs §2 "Interaction with Kickoff Call"):
//   • Strategic Content Creation is the SOLE trigger → Thryv Success Services
//     call only (kickoff hidden)
//   • SCC plus anything else that needs a kickoff → BOTH calls shown
//   • no SCC → Thryv Kickoff Call only
// Creative Call is gone (FULSAS-11317 folded it into Kickoff) so it is not
// modelled here.
//
// Both calls book through the same plans/onboarding POST (legacy's
// confirmThryvSuccessServices does exactly that) and only differ in the
// available-times appointmentType, the captured appointment_type, and the
// DELETE endpoint used to release the hold.
// ---------------------------------------------------------------------------
export const THRYV_SUCCESS_SERVICES_CODES = new Set([
  'addon-5-stratcontent-m2m',
])

export const CALL_TYPES = {
  kickoff: {
    key: 'kickoff',
    label: 'Thryv Kickoff Call',
    appointmentType: 'Onboarding', // cc/order/available-times ?appointmentType=
    appointmentTypeKey: 'onboarding', // scheduled_appointment.appointment_type + DELETE body.type
    cancelEndpoint: 'plans/onboarding',
  },
  'thryv-success-services': {
    key: 'thryv-success-services',
    label: 'Thryv Success Services Call',
    appointmentType: 'ThryvSuccessServices',
    appointmentTypeKey: 'thryv-success-services',
    cancelEndpoint: 'plans/thryv-success-services',
  },
}

function isNewTssItem(item) {
  return (
    (!item.alreadyIncluded &&
      THRYV_SUCCESS_SERVICES_CODES.has(item.productCode)) ||
    (item.addons ?? []).some(
      addon =>
        !addon.alreadyIncluded &&
        THRYV_SUCCESS_SERVICES_CODES.has(addon.productCode),
    )
  )
}

/**
 * The named calls this cart requires, in display order (kickoff first).
 * Empty array ⇔ itemsRequireScheduleCall(items) === false.
 *
 * @param {Array} items - one location's / one proposal option's items
 * @returns {Array<{key, label, appointmentType, appointmentTypeKey, cancelEndpoint}>}
 */
export function getRequiredCallTypes(
  items = [],
  {isTgpUpgradeEnabled = false} = {},
) {
  if (!Array.isArray(items) || items.length === 0) return []

  const needsTss = items.some(isNewTssItem)

  // Re-evaluate the kickoff rule with SCC stripped out so SCC alone never
  // forces a kickoff — mirrors legacy's isSCCOnlyTrigger re-run of
  // comparePlans without CODES_WITH_THRYV_SUCCESS_SERVICES_CALL.
  const itemsWithoutTss = items
    .filter(item => !THRYV_SUCCESS_SERVICES_CODES.has(item.productCode))
    .map(item =>
      item.addons
        ? {
            ...item,
            addons: item.addons.filter(
              addon => !THRYV_SUCCESS_SERVICES_CODES.has(addon.productCode),
            ),
          }
        : item,
    )
  const needsKickoff = itemsRequireScheduleCall(itemsWithoutTss, {
    isTgpUpgradeEnabled,
  })

  const result = []
  if (needsKickoff) result.push(CALL_TYPES.kickoff)
  if (needsTss) result.push(CALL_TYPES['thryv-success-services'])
  return result
}

/**
 * productGroupCodes for cc/order/available-times — the codes the backend uses
 * to pick the right onboarding service/staff pool. Derived from the SAME rule
 * as the gate so a cart that passes the gate never sends an empty list:
 * every qualifying center, top-level product, nested addon and entitlement.
 */
export function buildProductGroupCodes(items = []) {
  const codes = []
  const push = code => {
    if (code && !codes.includes(code)) codes.push(code)
  }

  ;(items ?? []).forEach(item => {
    if (!item?.productCode) return
    if (
      (item.productType === 'CENTER' &&
        !APPOINTMENT_EXEMPT_CENTER_CODES.has(item.productCode)) ||
      APPOINTMENT_REQUIRED_CODES.has(item.productCode) ||
      (THRYV_STARTER_CODES.includes(item.productCode) &&
        hasStarterOnboardingOptIn(items))
    ) {
      push(item.productCode)
    }
    ;(item.addons ?? []).forEach(addon => {
      if (
        APPOINTMENT_REQUIRED_CODES.has(addon.productCode) ||
        APPOINTMENT_REQUIRED_ENTITLEMENT_CODES.has(addon.productCode)
      ) {
        push(addon.productCode)
      }
    })
    ;(item.entitlements ?? []).forEach(ent => {
      if (APPOINTMENT_REQUIRED_ENTITLEMENT_CODES.has(ent.code)) push(ent.code)
    })
  })
  return codes
}
